import {
  CensusFieldCatalogItem,
  CompanyIgnoreRuleVersion,
  useActiveCompanyIgnoreRulesQuery,
  useCensusFieldCatalogQuery,
  useCompanyIgnoreRuleHistoryQuery,
} from '../../api/company-ignore-api-client'

export interface CompanyIgnoreData {
  fields: CensusFieldCatalogItem[]
  activeConfig: CompanyIgnoreRuleVersion | null | undefined
  versions: CompanyIgnoreRuleVersion[]
  // True until catalog + active config have both loaded. Version history is
  // display-only for the Version History panel, so it does not gate the form.
  isLoading: boolean
  // Set only when catalog or active config failed with no cached data to
  // fall back on.
  error: Error | null
  // History has its own loading/error so a history-only failure surfaces in
  // the Version History panel without blocking Save or misreporting active
  // config as failed.
  isHistoryLoading: boolean
  historyError: Error | null
}

// Wraps the requests that back the Company Ignore panel. Catalog + active
// config gate the editable form (Save needs both); version history is
// independent so it never blocks or gets confused with the form's state.
export function useCompanyIgnoreData(
  clientPublicKey: string
): CompanyIgnoreData {
  const {
    data: catalogFields,
    isLoading: isCatalogLoading,
    isError: isCatalogError,
    error: catalogError,
  } = useCensusFieldCatalogQuery(clientPublicKey)
  const {
    data: activeConfig,
    isLoading: isActiveLoading,
    isError: isActiveError,
    error: activeError,
  } = useActiveCompanyIgnoreRulesQuery(clientPublicKey)
  const {
    data: versions,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
    error: historyError,
  } = useCompanyIgnoreRuleHistoryQuery(clientPublicKey)

  // Refetch failures keep cached data but still set error — only treat as
  // fatal when there is no usable data to fall back on.
  const error =
    (isCatalogError && catalogFields === undefined ? catalogError : null) ??
    (isActiveError && activeConfig === undefined ? activeError : null)

  // Use "has data" rather than isSuccess: a background refetch failure (eg.
  // the invalidation right after a save) flips isSuccess to false even when
  // the previous data is still cached, which would otherwise re-lock the UI
  // in a permanent loading state with no error shown.
  const isReady = catalogFields !== undefined && activeConfig !== undefined

  return {
    fields: catalogFields ?? [],
    activeConfig,
    versions: versions ?? [],
    isLoading: !error && (!isReady || isCatalogLoading || isActiveLoading),
    error,
    isHistoryLoading: versions === undefined && isHistoryLoading,
    historyError:
      isHistoryError && versions === undefined ? historyError : null,
  }
}
